import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const PatientNavigationMenu = ({ 
  isCollapsed = false,
  onSymptomLogClick = () => {},
  className = ""
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSymptomModalOpen, setIsSymptomModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '/patient-dashboard',
      description: 'View your health overview'
    },
    {
      id: 'games',
      label: 'Cognitive Games',
      icon: 'Gamepad2',
      path: '/cognitive-games-section',
      description: 'Play brain training games'
    },
    {
      id: 'symptoms',
      label: 'Log Symptoms',
      icon: 'Plus',
      action: 'modal',
      description: 'Record daily symptoms'
    }
  ];

  const handleNavigation = (item) => {
    if (item?.action === 'modal') {
      setIsSymptomModalOpen(true);
      onSymptomLogClick();
    } else {
      navigate(item?.path);
    }
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  // Desktop Navigation
  const DesktopNavigation = () => (
    <nav className="hidden md:flex items-center space-x-1 bg-card border border-border rounded-lg p-1 shadow-soft">
      {navigationItems?.map((item) => (
        <button
          key={item?.id}
          onClick={() => handleNavigation(item)}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150
            ${isActiveRoute(item?.path) 
              ? 'bg-primary text-primary-foreground shadow-soft' 
              : 'text-foreground hover:bg-muted hover:text-foreground'
            }
            ${item?.action === 'modal' ? 'bg-accent hover:bg-accent/90 text-accent-foreground' : ''}
          `}
        >
          <Icon 
            name={item?.icon} 
            size={18} 
            color={isActiveRoute(item?.path) ? 'currentColor' : 'var(--color-muted-foreground)'}
          />
          {!isCollapsed && <span>{item?.label}</span>}
        </button>
      ))}
    </nav>
  );

  // Mobile Navigation
  const MobileNavigation = () => (
    <div className="md:hidden">
      {/* Mobile Menu Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="flex items-center space-x-2"
      >
        <Icon name="Menu" size={18} />
        <span>Menu</span>
      </Button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/20 z-[900]" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed top-16 left-4 right-4 bg-card border border-border rounded-lg shadow-soft-lg z-[950] animate-slide-in">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Navigation</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon name="X" size={18} />
                </Button>
              </div>
              
              <div className="space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => handleNavigation(item)}
                    className={`
                      w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors duration-150
                      ${isActiveRoute(item?.path) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted text-foreground'
                      }
                      ${item?.action === 'modal' ? 'bg-accent hover:bg-accent/90 text-accent-foreground' : ''}
                    `}
                  >
                    <Icon 
                      name={item?.icon} 
                      size={20} 
                      color="currentColor"
                    />
                    <div>
                      <div className="font-medium">{item?.label}</div>
                      <div className="text-xs opacity-75">{item?.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Symptom Logging Modal
  const SymptomModal = () => (
    isSymptomModalOpen && (
      <>
        <div 
          className="fixed inset-0 bg-black/50 z-[1100]" 
          onClick={() => setIsSymptomModalOpen(false)}
        />
        <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 md:w-96 bg-card border border-border rounded-lg shadow-soft-lg z-[1100] animate-scale-in">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Log Symptoms</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSymptomModalOpen(false)}
              >
                <Icon name="X" size={18} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Quick symptom logging will be implemented here. This modal provides immediate access to symptom tracking functionality.
              </p>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsSymptomModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    setIsSymptomModalOpen(false);
                    // Handle symptom logging
                  }}
                  className="flex-1"
                >
                  Save Symptoms
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );

  return (
    <div className={`relative ${className}`}>
      <DesktopNavigation />
      <MobileNavigation />
      <SymptomModal />
    </div>
  );
};

export default PatientNavigationMenu;